<template>
  <el-dialog
      :visible.sync="dialogVisible"
      width="90%"
      :before-close="handleClose"
      custom-class="order-confirm-dialog"
      :close-on-click-modal="false"
      append-to-body
  >
    <!-- 支付成功页面 -->
    <div v-if="paymentSuccess" class="payment-success">
      <div class="success-content">
        <div class="success-icon">
          <i class="el-icon-success"></i>
        </div>
        <h2 class="success-title">
          {{ $t("OrderConfirmDialog.paymentComplete") }}
        </h2>
        <a href="#" class="view-link" data-guide="goPersonalCenter" @click.prevent="goToUserCenter">
          {{ $t("OrderConfirmDialog.viewInUserCenter") }}
        </a>
      </div>
    </div>

    <!-- 订单确认页面：使用 editableOrderInfo，避免直接修改父组件传入的 orderInfo -->
    <div
        v-else-if="editableOrderInfo && editableOrderInfo.length > 0"
        class="order-container"
    >
      <div class="order-left">
        <!-- 收货信息 -->
        <section class="section address-section">
          <div class="section-header">
            <span class="section-title">
              {{ $t("OrderConfirmDialog.receiverInfo") }}
            </span>
            <el-button type="text" icon="el-icon-edit" @click="openEditUserDialog">
              {{ $t("OrderConfirmDialog.editUserInfo") }}
            </el-button>
          </div>

          <div class="address-form">
            <el-form
                ref="receiverForm"
                :model="receiverInfo"
                :rules="receiverRules"
                label-width="90px"
            >
              <el-row>
                <el-col :span="10">
                  <el-form-item
                      :label="$t('OrderConfirmDialog.name')"
                      prop="name"
                  >
                    <el-input
                        v-model.trim="receiverInfo.name"
                        :placeholder="$t('OrderConfirmDialog.namePlaceholder')"
                    />
                  </el-form-item>
                </el-col>

                <el-col :span="10">
                  <el-form-item
                      :label="$t('OrderConfirmDialog.phone')"
                      prop="phone"
                  >
                    <el-input
                        v-model.trim="receiverInfo.phone"
                        :placeholder="$t('OrderConfirmDialog.phonePlaceholder')"
                    />
                  </el-form-item>
                </el-col>
              </el-row>

              <el-row>
                <el-col :span="10">
                  <el-form-item
                      :label="$t('OrderConfirmDialog.email')"
                      prop="email"
                  >
                    <el-input
                        v-model.trim="receiverInfo.email"
                        :placeholder="$t('OrderConfirmDialog.emailPlaceholder')"
                    />
                  </el-form-item>
                </el-col>

                <el-col :span="10">
                  <el-form-item
                      :label="$t('OrderConfirmDialog.company')"
                      prop="company"
                  >
                    <el-input
                        v-model.trim="receiverInfo.company"
                        :placeholder="$t('OrderConfirmDialog.companyPlaceholder')"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </div>
        </section>

        <!-- 确认订单信息 -->
        <section class="section order-section">
          <div class="section-header">
            <div class="section-title">
              {{ $t("OrderConfirmDialog.confirmOrderInfo") }}
            </div>
          </div>

          <div class="order-table">
            <div class="table-header">
              <span class="col-product">
                {{ $t("OrderConfirmDialog.productInfo") }}
              </span>
              <span class="col-detail">
                {{ $t("OrderConfirmDialog.productDetail") }}
              </span>
              <span class="col-duration">
                {{ $t("OrderConfirmDialog.purchaseDuration") }}
              </span>
              <span class="col-quantity">
                {{ $t("OrderConfirmDialog.purchaseQuantity") }}
              </span>
              <span class="col-price">
                {{ $t("OrderConfirmDialog.price") }}
              </span>
            </div>

            <div class="table-body">
              <div
                  v-for="(item, index) in editableOrderInfo"
                  :key="item.id || index"
                  class="order-item"
              >
                <div class="col-product">
                  <div class="product-info">
                    <div class="product-logo">
                      <img :src="productIcon(item)" />
                    </div>
                    <span
                        class="product-name"
                        :title="
                          item.production?.name ||
                          item.productInfo?.typeItem?.name ||
                          item.productInfo?.name
                        "
                    >
                      {{
                        item.production?.name ||
                        item.productInfo?.typeItem?.name ||
                        item.productInfo?.name
                      }}
                    </span>
                  </div>
                </div>

                <div class="col-detail">
                  <span class="detail-text">
                    {{ item.productInfo?.name }}
                  </span>
                </div>

                <!-- 购买时长：数字或单位变化后，自动换算 4周=1月、3月=1季、2季=半年 -->
                <div class="col-duration">
                  <div class="duration-editor">
                    <el-input-number
                        v-model="item.duration"
                        :min="1"
                        :max="99"
                        size="mini"
                        controls-position="right"
                        :disabled="isFree || showPayment"
                        @change="handleDurationChange(item)"
                    />

                    <el-select
                        v-model="item.durationunion"
                        size="mini"
                        class="duration-unit-select"
                        :disabled="isFree || showPayment"
                        @change="handleDurationChange(item)"
                    >
                      <el-option
                          v-for="option in durationUnitOptions"
                          :key="option.value"
                          :label="option.label"
                          :value="option.value"
                      />
                    </el-select>
                  </div>
                </div>

                <!-- 购买数量：只影响总价，不参与时长单位换算 -->
                <div class="col-quantity">
                  <el-input-number
                      v-model="item.quantity"
                      :min="1"
                      :max="99"
                      size="mini"
                      controls-position="right"
                      :disabled="isFree || showPayment"
                      @change="handleQuantityChange"
                  />
                </div>

                <!-- 价格保持原逻辑：当前单位单价 * 时长 * 数量 -->
                <div class="col-price">
                  <div class="price-info">
                    <span class="current-price">
                      ¥{{ getItemTotalPrice(item) }}
                    </span>

                    <div
                        v-if="retailerProductids.includes(getItemProductId(item))"
                        class="discount-info"
                    >
                      <span class="discount-label">
                        {{ $t("OrderConfirmDialog.inviteCodeDiscount") }}
                      </span>
                      <span class="discount-amount">
                        -¥{{ getItemDiscountAmount(item) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 邀请码 -->
          <div v-if="!isFree" class="form-row">
            <span class="form-label">
              {{ $t("OrderConfirmDialog.inviteCode") }}
            </span>
            <el-input
                v-model.trim="inviteCode"
                :placeholder="$t('OrderConfirmDialog.inviteCodePlaceholder')"
                class="invite-input"
                size="small"
                @input="handleInviteCodeInput"
                @blur="checkCode"
            />
          </div>

          <!-- 发票 -->
          <div v-if="!isFree" class="form-row">
            <span class="form-label">
              {{ $t("OrderConfirmDialog.invoice") }}
            </span>
            <label class="fake-checkbox" @click="selectInvoiceOption('need')">
              <span class="checkbox-icon" :class="{ checked: invoiceChoice === 'need' }">
                <i v-if="invoiceChoice === 'need'" class="el-icon-check"></i>
              </span>
              <span class="checkbox-label">开发票</span>
            </label>

            <label class="fake-checkbox" @click="selectInvoiceOption('none')">
              <span class="checkbox-icon" :class="{ checked: invoiceChoice === 'none' }">
                <i v-if="invoiceChoice === 'none'" class="el-icon-check"></i>
              </span>
              <span class="checkbox-label">不开票</span>
            </label>
          </div>

          <transition name="fade">
            <div v-if="invoiceChoice === 'need'" class="invoice-detail">
              <div class="invoice-row invoice-profile-select-row">
                <span class="form-label">开票信息</span>
                <el-button size="mini" type="primary" plain @click="openInvoiceProfileDialog">
                  {{ selectedInvoiceProfile ? '更换开票信息' : '选择开票信息' }}
                </el-button>
              </div>

              <div v-if="selectedInvoiceProfile" class="invoice-profile-preview">
                <div class="preview-row">
                  <span class="preview-label">发票类型</span>
                  <span class="preview-value">{{ selectedInvoiceProfile.invoiceType === '1' ? '专票' : '普票' }}</span>
                </div>
                <div class="preview-row">
                  <span class="preview-label">开票名称</span>
                  <span class="preview-value">{{ selectedInvoiceProfile.buyerName || '-' }}</span>
                </div>
                <div class="preview-row">
                  <span class="preview-label">纳税人识别号</span>
                  <span class="preview-value">{{ selectedInvoiceProfile.buyerTaxNo || '-' }}</span>
                </div>
                <div class="preview-row">
                  <span class="preview-label">邮箱</span>
                  <span class="preview-value">{{ selectedInvoiceProfile.email || '-' }}</span>
                </div>
                <div class="preview-row">
                  <span class="preview-label">联系方式</span>
                  <span class="preview-value">{{ selectedInvoiceProfile.buyerPhone || '-' }}</span>
                </div>
                <div class="preview-row">
                  <span class="preview-label">开户信息</span>
                  <span class="preview-value">{{ formatInvoiceBankInfo(selectedInvoiceProfile) }}</span>
                </div>
                <div class="preview-row">
                  <span class="preview-label">地址</span>
                  <span class="preview-value">{{ selectedInvoiceProfile.buyerAddress || '-' }}</span>
                </div>
              </div>
            </div>
          </transition>

          <!-- 订单备注 -->
          <div class="form-row">
            <span class="form-label">
              {{ $t("OrderConfirmDialog.orderRemark") }}
            </span>
            <el-input
                v-model="orderRemark"
                type="textarea"
                :rows="2"
                size="small"
                :placeholder="$t('OrderConfirmDialog.orderRemarkPlaceholder')"
                maxlength="200"
                show-word-limit
            />
          </div>

          <div class="form-actions">
            <el-button
                type="primary"
                size="small"
                :loading="submitting"
                :disabled="submitting"
                @click="handleConfirm"
            >
              {{ $t("OrderConfirmDialog.confirm") }}
            </el-button>

            <el-button size="small" @click="handleCancel">
              {{ $t("OrderConfirmDialog.cancel") }}
            </el-button>
          </div>
        </section>
      </div>

      <!-- 右侧付款详情 -->
      <transition name="slide-left">
        <div class="order-right">
          <div class="payment-section">
            <h3 class="payment-title">
              {{ $t("OrderConfirmDialog.paymentDetails") }}
            </h3>

            <p class="product-count">
              {{
                $t("OrderConfirmDialog.totalProducts", {
                  count: editableOrderInfo.length,
                })
              }}
            </p>

            <div class="price-summary">
              <div class="price-row">
                <span>{{ $t("OrderConfirmDialog.productTotal") }}</span>
                <span class="price" :class="{ 'free-text': isFree }">
                  ¥{{ totalPrice }}
                </span>
              </div>

              <div
                  v-if="discountAmount > 0 && !isFree"
                  class="price-row discount"
              >
                <span>{{ $t("OrderConfirmDialog.inviteCodeDiscount") }}</span>
                <span class="price discount-price">
                  -¥{{ discountAmount }}
                </span>
              </div>

              <div class="price-row total">
                <span>{{ $t("OrderConfirmDialog.total") }}</span>
                <span
                    class="price final-price"
                    :class="{ 'free-text': isFree }"
                >
                  ¥{{ finalPrice }}
                </span>
              </div>
            </div>

            <template v-if="!isFree">
              <div v-if="showPayment" class="qrcode-section">
                <div class="qrcode-wrapper">
                  <img
                      v-if="paymentQrcode"
                      :src="paymentQrcode"
                      alt="支付二维码"
                  />
                  <div v-else class="qrcode-loading">
                    <i class="el-icon-loading"></i>
                  </div>
                </div>
                <p class="qrcode-tip">
                  {{ $t("OrderConfirmDialog.scanToPay") }}
                </p>
              </div>

              <div v-if="showPayment" class="payment-methods">
                <div
                    v-for="method in paymentMethodsList"
                    :key="method.value"
                    class="method-item"
                >
                  <img :src="method.icon" :alt="method.label" />
                  <span>{{ method.label }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </transition>
    </div>

    <el-dialog
      :visible.sync="invoiceProfileDialogVisible"
      width="760px"
      title="选择开票信息"
      append-to-body
      :close-on-click-modal="false"
      custom-class="invoice-profile-select-dialog"
    >
      <div class="invoice-profile-dialog-actions">
        <el-button type="primary" size="small" @click="openInvoiceProfileForm()">新增开票信息</el-button>
      </div>

      <div v-if="invoiceProfiles.length" class="invoice-profile-card-list">
        <div
          v-for="item in invoiceProfiles"
          :key="item.id"
          class="invoice-profile-card"
          :class="{ active: invoiceProfileDraftId === item.id }"
          @click="selectInvoiceProfile(item)"
        >
          <span
            class="profile-select-indicator"
            :class="{ checked: invoiceProfileDraftId === item.id }"
          >
            <i
              v-if="invoiceProfileDraftId === item.id"
              class="el-icon-check"
            ></i>
          </span>

          <div class="profile-head">
            <div class="profile-title-wrap">
              <span class="profile-title">{{ item.buyerName || '-' }}</span>
              <el-tag size="mini" :type="item.invoiceType === '1' ? 'danger' : 'success'">
                {{ item.invoiceType === '1' ? '专票' : '普票' }}
              </el-tag>
              <el-tag v-if="item.isMain === 'Y'" size="mini" type="warning">默认</el-tag>
            </div>
            <div class="profile-actions">
              <el-button type="text" @click.stop="openInvoiceProfileForm(item)">编辑</el-button>
              <el-button v-if="item.isMain !== 'Y'" type="text" @click.stop="setDefaultInvoiceProfileAction(item)">设为默认</el-button>
              <el-button type="text" class="danger" @click.stop="removeInvoiceProfileAction(item)">删除</el-button>
            </div>
          </div>
          <div class="profile-body">
            <div class="profile-row"><span class="label">纳税人识别号</span><span class="value">{{ item.buyerTaxNo || '-' }}</span></div>
            <div class="profile-row"><span class="label">邮箱</span><span class="value">{{ item.email || '-' }}</span></div>
            <div class="profile-row"><span class="label">联系方式</span><span class="value">{{ item.buyerPhone || '-' }}</span></div>
            <div class="profile-row"><span class="label">开户信息</span><span class="value">{{ formatInvoiceBankInfo(item) }}</span></div>
          </div>
        </div>
      </div>
      <el-empty v-else description="暂无开票信息" />

      <span slot="footer" class="dialog-footer">
        <el-button @click="invoiceProfileDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmInvoiceProfileSelection">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :visible.sync="invoiceProfileFormVisible"
      width="620px"
      :title="invoiceProfileForm.id ? '编辑开票信息' : '新增开票信息'"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form ref="invoiceProfileFormRef" :model="invoiceProfileForm" :rules="invoiceProfileRules" label-width="110px">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="开票名称" prop="buyerName">
              <el-input v-model.trim="invoiceProfileForm.buyerName" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="发票类型" prop="invoiceType">
              <el-select v-model="invoiceProfileForm.invoiceType" style="width: 100%" clearable>
                <el-option label="专票" value="1" />
                <el-option label="普票" value="2" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="纳税人识别号" prop="buyerTaxNo">
              <el-input v-model.trim="invoiceProfileForm.buyerTaxNo" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="邮箱" prop="email">
              <el-input v-model.trim="invoiceProfileForm.email" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系方式">
              <el-input v-model.trim="invoiceProfileForm.buyerPhone" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="地址">
              <el-input v-model.trim="invoiceProfileForm.buyerAddress" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="开户行">
              <el-input v-model.trim="invoiceProfileForm.buyerBankName" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="开户账号">
              <el-input v-model.trim="invoiceProfileForm.buyerBankAccount" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input v-model.trim="invoiceProfileForm.remark" type="textarea" :rows="2" maxlength="255" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="默认开票信息">
              <el-switch v-model="invoiceProfileMainSwitch" @change="syncInvoiceProfileMainStatus" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="invoiceProfileFormVisible = false">取消</el-button>
        <el-button type="primary" :loading="invoiceProfileSubmitting" @click="submitInvoiceProfileForm">确定</el-button>
      </span>
    </el-dialog>

    <!-- 修改用户信息 -->
    <el-dialog
      :visible.sync="editUserDialogVisible"
      width="500px"
      :title="$t('OrderConfirmDialog.editUserInfoTitle')"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form ref="editUserFormRef" :model="editUserForm" :rules="editUserRules" label-width="90px">
        <el-form-item :label="$t('OrderConfirmDialog.name')" prop="username">
          <el-input v-model.trim="editUserForm.username" :placeholder="$t('OrderConfirmDialog.namePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('OrderConfirmDialog.phone')" prop="phonenum">
          <el-input v-model.trim="editUserForm.phonenum" :placeholder="$t('OrderConfirmDialog.phonePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('OrderConfirmDialog.email')" prop="email">
          <el-input v-model.trim="editUserForm.email" :placeholder="$t('OrderConfirmDialog.emailPlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('OrderConfirmDialog.company')" prop="companyname">
          <el-input v-model.trim="editUserForm.companyname" :placeholder="$t('OrderConfirmDialog.companyPlaceholder')" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editUserDialogVisible = false">{{ $t("OrderConfirmDialog.cancel") }}</el-button>
        <el-button type="primary" :loading="editUserSaving" @click="saveEditUserInfo">
          {{ $t("OrderConfirmDialog.confirm") }}
        </el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import {
  createOrder,
  getCmbQrCode,
  getOrderMes,
  C_retailers,
} from "@/assets/API/pay";
import { checkBlacklist } from "@/assets/API/login";
import {
  getInvoiceProfileList,
  modifyInvoiceProfile,
  removeInvoiceProfile,
  setDefaultInvoiceProfile,
} from "@/assets/API/invoiceProfile";
import request from "@/utils/request";
import define from "@/utils/define";
import {
  startAfterPayGuidePersonal,
  destroyAfterPayGuide,
  getAfterPayPhase,
  setAfterPayPhase,
} from "@/utils/guideTour";

const baseUrl = define.APIURl;

const DEFAULT_INVOICE_FORM = {
  type: "electronic",
  content: "detail",
  headerType: "company",

  // 发票抬头，页面展示公司名称/发票抬头，不展示统一信用代码。
  company: "",

  // 下单页填写的统一社会信用代码。默认从用户中心带出，但不会自动同步回用户中心。
  ratenumber: "",

  // 兼容旧字段，避免旧逻辑仍读取 companysocialcreditcode。
  companysocialcreditcode: "",

  address: "",
  phone: "",
  email: "",
};

const DEFAULT_INVOICE_PROFILE_FORM = {
  id: "",
  customerId: "",
  buyerName: "",
  buyerTaxNo: "",
  buyerPhone: "",
  buyerBankName: "",
  buyerBankAccount: "",
  buyerAddress: "",
  email: "",
  invoiceType: "",
  remark: "",
  isMain: "N",
};

export default {
  name: "OrderConfirmDialog",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    orderInfo: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      paymentSuccess: false,
      showPayment: false,
      paying: false,
      submitting: false,
      orderStatus: {},

      // 本地订单副本：用于确认页编辑时长和数量，避免直接修改 props。
      editableOrderInfo: [],

      receiverInfo: {
        name: "",
        phone: "",
        email: "",
        company: "",
        address: "",
      },

      inviteCode: "",
      retailerProductids: [],
      retailCount: 1,
      discountAmount: 0,

      orderRemark: "",
      needInvoice: false,
      invoiceChoice: "",
      selectedInvoiceProfileId: "",
      invoiceProfileDraftId: "",
      selectedInvoiceProfile: null,
      invoiceProfiles: [],
      invoiceProfileDialogVisible: false,
      invoiceProfileFormVisible: false,
      invoiceProfileSubmitting: false,
      invoiceProfileMainSwitch: false,
      invoiceProfileForm: { ...DEFAULT_INVOICE_PROFILE_FORM },
      invoiceProfileRules: {
        buyerName: [{ required: true, message: "请输入开票名称", trigger: "blur" }],
        buyerTaxNo: [{ required: true, message: "请输入纳税人识别号", trigger: "blur" }],
        email: [{ required: true, message: "请输入邮箱", trigger: "blur" }],
        invoiceType: [{ required: true, message: "请选择发票类型", trigger: "change" }],
      },
      invoiceForm: { ...DEFAULT_INVOICE_FORM },
      creditUpdating: false,

      paymentQrcode: "",
      intervalPay: null,

      // 修改用户信息弹窗
      editUserDialogVisible: false,
      editUserSaving: false,
      editUserForm: {
        id: "",
        username: "",
        phonenum: "",
        email: "",
        companyname: "",
      },
    };
  },

  computed: {
    language() {
      return this.$i18n.locale;
    },

    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },

    isFree() {
      return (
          this.editableOrderInfo[0] &&
          this.editableOrderInfo[0].isFree === true
      );
    },

    isSocialCreditCodeValid() {
      if (!this.invoiceForm.ratenumber) return false;
      return this.validateSocialCreditCode(this.invoiceForm.ratenumber);
    },

    receiverRules() {
      return {
        name: [
          {
            required: true,
            message: this.$t("OrderConfirmDialog.nameRequired"),
            trigger: "blur",
          },
        ],
        phone: [
          {
            required: true,
            message: this.$t("OrderConfirmDialog.phoneRequired"),
            trigger: "blur",
          },
          {
            pattern: /^1[3-9]\d{9}$/,
            message: this.$t("OrderConfirmDialog.phoneInvalid"),
            trigger: "blur",
          },
        ],
      };
    },

    editUserRules() {
      return {
        username: [
          {
            required: true,
            message: this.$t("OrderConfirmDialog.nameRequired"),
            trigger: "blur",
          },
        ],
        phonenum: [
          {
            required: true,
            message: this.$t("OrderConfirmDialog.phoneRequired"),
            trigger: "blur",
          },
          {
            pattern: /^1[3-9]\d{9}$/,
            message: this.$t("OrderConfirmDialog.phoneInvalid"),
            trigger: "blur",
          },
        ],
        email: [
          {
            required: true,
            message: this.$t("OrderConfirmDialog.emailRequired"),
            trigger: "blur",
          },
          {
            type: "email",
            message: this.$t("OrderConfirmDialog.emailInvalid"),
            trigger: "blur",
          },
        ],
      };
    },

    durationMap() {
      return {
        week: this.$t("ShoppingCartDialog.week"),
        month: this.$t("ShoppingCartDialog.month"),
        season: this.$t("ShoppingCartDialog.quarter"),
        halfyear: this.$t("ShoppingCartDialog.halfyear"),
      };
    },

    durationUnitOptions() {
      return [
        { value: "week", label: this.durationMap.week || "周" },
        { value: "month", label: this.durationMap.month || "月" },
        { value: "season", label: this.durationMap.season || "季" },
        { value: "halfyear", label: this.durationMap.halfyear || "半年" },
      ];
    },

    // 自动换算规则。只有整除时才换算。
    durationConvertConfig() {
      return [
        { value: "week", convertThreshold: 4, nextUnit: "month" },
        { value: "month", convertThreshold: 3, nextUnit: "season" },
        { value: "season", convertThreshold: 2, nextUnit: "halfyear" },
        { value: "halfyear", convertThreshold: null, nextUnit: null },
      ];
    },

    paymentMethodsList() {
      return [
        {
          value: "alipay",
          label: this.$t("OrderConfirmDialog.alipay"),
          icon: require("@/assets/image/pay/alipay.png"),
        },
        {
          value: "wechat",
          label: this.$t("OrderConfirmDialog.wechat"),
          icon: require("@/assets/image/pay/wechat.png"),
        },
        {
          value: "unionpay",
          label: this.$t("OrderConfirmDialog.unionpay"),
          icon: require("@/assets/image/pay/unionpay.png"),
        },
        {
          value: "credit",
          label: this.$t("OrderConfirmDialog.creditCard"),
          icon: require("@/assets/image/pay/credit.png"),
        },
      ];
    },

    // 商品总价保持原有计算方式：单价 * 时长 * 数量。
    totalPrice() {
      if (!this.editableOrderInfo.length) {
        return "0.00";
      }

      const total = this.editableOrderInfo.reduce((sum, item) => {
        return sum + Number(this.getItemTotalPrice(item));
      }, 0);

      return total.toFixed(2);
    },

    finalPrice() {
      if (this.isFree) return "0.00";

      const value =
          Number(this.totalPrice || 0) - Number(this.discountAmount || 0);

      return Math.max(value, 0).toFixed(2);
    },
  },

  watch: {
    visible: {
      handler(val) {
        if (val) {
          this.initData();
        } else {
          this.stopPolling();
        }
      },
      immediate: true,
    },

    orderInfo: {
      handler() {
        if (this.visible) {
          this.createEditableOrderInfo();
        }
      },
      deep: true,
    },
  },

  beforeDestroy() {
    destroyAfterPayGuide();
    this.stopPolling();
  },

  methods: {
    async initData() {
      this.paymentSuccess = false;
      this.showPayment = false;
      this.inviteCode = "";
      this.discountAmount = 0;
      this.needInvoice = false;
      this.invoiceChoice = "";
      this.selectedInvoiceProfileId = "";
      this.invoiceProfileDraftId = "";
      this.selectedInvoiceProfile = null;
      this.invoiceProfiles = [];
      this.invoiceProfileDialogVisible = false;
      this.invoiceProfileFormVisible = false;
      this.invoiceProfileSubmitting = false;
      this.invoiceProfileMainSwitch = false;
      this.invoiceProfileForm = { ...DEFAULT_INVOICE_PROFILE_FORM };
      this.invoiceForm = { ...DEFAULT_INVOICE_FORM };
      this.orderRemark = "";
      this.receiverInfo = {
        name: "",
        phone: "",
        email: "",
        company: "",
        address: "",
      };
      this.orderStatus = {};
      this.paymentQrcode = "";
      this.stopPolling();

      this.createEditableOrderInfo();
      await this.fetchUserMes();
    },

    createEditableOrderInfo() {
      this.editableOrderInfo = (this.orderInfo || []).map((item) => {
        const newItem = {
          ...item,
          duration: Number(item.duration) || 1,
          durationunion: this.normalizeDurationUnit(
              item.durationunion || "week"
          ),
          quantity: Number(item.quantity || item.number) || 1,
        };

        // 初次进入确认页时，也执行一次换算。
        this.normalizeDurationItem(newItem);
        return newItem;
      });
    },

    normalizeDurationUnit(unit) {
      return unit === "quarter" ? "season" : unit || "week";
    },

    normalizeDurationItem(item) {
      if (!item) return;

      const calculate = (unit, duration) => {
        const currentUnit = this.normalizeDurationUnit(unit);
        const currentDuration = Number(duration) || 1;

        const config = this.durationConvertConfig.find(
            (configItem) => configItem.value === currentUnit
        );

        if (
            !config ||
            !config.convertThreshold ||
            !config.nextUnit ||
            currentDuration < config.convertThreshold
        ) {
          return {
            durationunion: currentUnit,
            duration: currentDuration,
          };
        }

        // 不能整除则不转换，例如 5 周仍然是 5 周。
        if (currentDuration % config.convertThreshold !== 0) {
          return {
            durationunion: currentUnit,
            duration: currentDuration,
          };
        }

        return calculate(
            config.nextUnit,
            currentDuration / config.convertThreshold
        );
      };

      const result = calculate(item.durationunion, item.duration);

      item.durationunion = result.durationunion;
      item.duration = result.duration;
    },

    handleDurationChange(item) {
      this.normalizeDurationItem(item);
      this.recalculateDiscountAmount();
    },

    handleQuantityChange() {
      this.recalculateDiscountAmount();
    },

    productIcon(item) {
      // 直接购买的数据已经在 Buy.vue 中完成图片地址解析，付款弹框直接使用。
      if (!item.production) {
        return item.productInfo?.typeItem?.icon || null;
      }

      // 购物车结算使用接口返回的原始产品数据，在这里仅解析该原始图标。
      const icon = item.production.icon;

      if (!icon) return null;

      // 已经是完整 URL（http/https 开头），直接返回
      if (/^https?:\/\//i.test(icon)) return icon;

      // 兼容已经是纯路径（如 /api/file/...）的情况
      if (typeof icon === "string" && icon.trim().startsWith("/")) {
        return `${baseUrl}${icon}`;
      }

      // 兼容旧数据：JSON 字符串格式 [{"url":"..."}]
      try {
        const pathJson = JSON.parse(icon);
        if (Array.isArray(pathJson) && pathJson[0] && pathJson[0].url) {
          const url = pathJson[0].url;
          return /^https?:\/\//i.test(url) ? url : `${baseUrl}${url}`;
        }
        if (pathJson && pathJson.url) {
          return /^https?:\/\//i.test(pathJson.url) ? pathJson.url : `${baseUrl}${pathJson.url}`;
        }
      } catch (error) {
        /* 非合法 JSON，返回原值兜底 */
      }
      return icon;
    },

    getItemUnitPrice(item) {
      if (!item || !item.productInfo) return 0;

      const durationunion = this.normalizeDurationUnit(item.durationunion);

      const priceFieldMap = {
        week: "weekprice",
        month: "monthprice",
        season: "seasonprice",
        halfyear: "halfyearprice",
      };

      const priceField = priceFieldMap[durationunion] || "weekprice";

      return Number(item.productInfo[priceField]) || 0;
    },

    getItemTotalPrice(item) {
      const unitPrice = this.getItemUnitPrice(item);
      const duration = Number(item.duration) || 1;
      const quantity = Number(item.quantity || item.number) || 1;

      return (unitPrice * duration * quantity).toFixed(2);
    },

    getItemProductId(item) {
      return String(
          item?.productInfo?.productId ||
          item?.productid ||
          item?.production?.id ||
          ""
      ).trim();
    },

    getItemDiscountAmount(item) {
      const productId = this.getItemProductId(item);
      if (!productId || !this.retailerProductids.includes(productId)) {
        return "0.00";
      }

      const itemTotal = Number(this.getItemTotalPrice(item));
      return Math.max(itemTotal - itemTotal * this.retailCount, 0).toFixed(2);
    },

    recalculateDiscountAmount() {
      if (!this.inviteCode || !this.retailerProductids.length) {
        this.discountAmount = 0;
        return;
      }

      const amount = this.editableOrderInfo.reduce((sum, item) => {
        return sum + Number(this.getItemDiscountAmount(item));
      }, 0);
      this.discountAmount = Number(amount.toFixed(2));
    },

    clearInviteCode() {
      this.inviteCode = "";
      this.retailerProductids = [];
      this.retailCount = 1;
      this.discountAmount = 0;
    },

    handleInviteCodeInput() {
      // 输入内容发生变化后，旧的匹配结果立即失效；保留当前输入等待失焦校验。
      this.retailerProductids = [];
      this.retailCount = 1;
      this.discountAmount = 0;
    },

    async fetchUserMes() {
      try {
        // 重新拉取用户中心信息，避免确认页使用旧的 store 数据。
        await this.$store.dispatch("user/getInfo");

        const userInfo = this.$store?.state?.user?.userInfo || {};

        this.receiverInfo = {
          name: userInfo.username || "",
          phone: userInfo.phonenum || "",
          email: userInfo.email || "",
          company: userInfo.companyname || "",
          address: userInfo.address || "",
        };

        this.invoiceForm = {
          ...this.invoiceForm,
          company: userInfo.companyname || "",

          // 默认带出用户中心统一信用代码，方便下单使用；
          // 但后续用户在下单页修改后，不会自动同步回用户中心。
          ratenumber: userInfo.companysocialcreditcode || "",
          companysocialcreditcode: userInfo.companysocialcreditcode || "",

          address: userInfo.address || "",
          phone: userInfo.phonenum || "",
          email: userInfo.email || "",
        };
      } catch (error) {
        console.warn("获取用户信息失败", error);
      }
    },

    getCurrentUserId() {
      const userInfo = this.$store?.state?.user?.userInfo || {};
      const userId = userInfo.id || userInfo.userId || "";
      return String(userId || "");
    },

    // 打开用户信息修改弹窗，预填当前用户信息
    openEditUserDialog() {
      const userInfo = this.$store?.state?.user?.userInfo || {};
      this.editUserForm = {
        id: userInfo.id || "",
        username: userInfo.username || "",
        phonenum: userInfo.phonenum || "",
        email: userInfo.email || "",
        companyname: userInfo.companyname || "",
      };
      this.editUserDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.editUserFormRef) {
          this.$refs.editUserFormRef.clearValidate();
        }
      });
    },

    // 保存用户信息：更新后台后重新拉取用户信息，同步刷新确认页展示
    saveEditUserInfo() {
      this.$refs.editUserFormRef.validate(async (valid) => {
        if (!valid) return;
        if (!this.editUserForm.id) {
          this.$message.warning("未获取到用户信息，请重新登录后再试");
          return;
        }
        this.editUserSaving = true;
        try {
          const userInfo = this.$store?.state?.user?.userInfo || {};
          const res = await request({
            url: "/api/business/C_customers/" + this.editUserForm.id,
            method: "PUT",
            data: { ...userInfo, ...this.editUserForm },
          });
          if (res.code !== 200) {
            this.$message.error(res.msg || this.$t("OrderConfirmDialog.userInfoSaveFailed"));
            return;
          }
          // fetchUserMes 内部会重新调接口刷新 store + localStorage，并回填本页表单
          await this.fetchUserMes();
          this.editUserDialogVisible = false;
          this.$message.success(this.$t("OrderConfirmDialog.userInfoSaveSuccess"));
        } catch (error) {
          console.warn("保存用户信息失败", error);
          this.$message.error(this.$t("OrderConfirmDialog.userInfoSaveFailed"));
        } finally {
          this.editUserSaving = false;
        }
      });
    },

    async selectInvoiceOption(type) {
      this.invoiceChoice = type;
      this.needInvoice = type === "need";
      if (type === "need") {
        const result = await this.loadInvoiceProfiles();
        if (!result || !result.hasSelectedProfile) {
          this.invoiceProfileDialogVisible = true;
          this.$message.warning("请选择一条开票信息");
        }
      }
    },

    formatInvoiceBankInfo(profile) {
      const bankName = profile?.buyerBankName || "-";
      const bankAccount = profile?.buyerBankAccount || "-";
      return `${bankName} / ${bankAccount}`;
    },

    buildInvoiceFormByProfile(profile) {
      if (!profile) return { ...DEFAULT_INVOICE_FORM };
      return {
        ...DEFAULT_INVOICE_FORM,
        type: String(profile.invoiceType) === "1" ? "special" : "electronic",
        headerType: "company",
        company: profile.buyerName || "",
        ratenumber: profile.buyerTaxNo || "",
        companysocialcreditcode: profile.buyerTaxNo || "",
        address: profile.buyerAddress || "",
        phone: profile.buyerPhone || "",
        email: profile.email || "",
        buyerBankName: profile.buyerBankName || "",
        buyerBankAccount: profile.buyerBankAccount || "",
      };
    },

    async loadInvoiceProfiles() {
      const customerId = this.getCurrentUserId();
      if (!customerId) {
        this.$message.warning("登录状态异常，请重新登录");
        return { hasSelectedProfile: false };
      }
      const res = await getInvoiceProfileList({ customerId });
      if (res.code !== 200) {
        this.$message.error(res.msg || "获取开票信息失败");
        return { hasSelectedProfile: false };
      }

      this.invoiceProfiles = Array.isArray(res.data) ? res.data : [];

      const selected = this.selectedInvoiceProfileId
        ? this.invoiceProfiles.find((item) => item.id === this.selectedInvoiceProfileId)
        : null;

      const defaultItem = this.invoiceProfiles.find((item) => item.isMain === "Y");
      const activeItem = selected || defaultItem || null;

      if (activeItem) {
        this.selectedInvoiceProfileId = activeItem.id;
        this.selectedInvoiceProfile = { ...activeItem };
        this.invoiceForm = this.buildInvoiceFormByProfile(activeItem);
        return { hasSelectedProfile: true };
      }

      this.selectedInvoiceProfileId = "";
      this.selectedInvoiceProfile = null;
      this.invoiceForm = { ...DEFAULT_INVOICE_FORM };
      return { hasSelectedProfile: false };
    },

    async openInvoiceProfileDialog() {
      await this.loadInvoiceProfiles();
      this.invoiceProfileDraftId = this.selectedInvoiceProfileId || "";
      this.invoiceProfileDialogVisible = true;
    },

    selectInvoiceProfile(item) {
      this.invoiceProfileDraftId = item.id;
    },

    confirmInvoiceProfileSelection() {
      if (!this.invoiceProfileDraftId) {
        this.$message.warning("请选择开票信息");
        return;
      }
      const selected = this.invoiceProfiles.find(
        (item) => item.id === this.invoiceProfileDraftId
      );
      if (!selected) {
        this.$message.warning("开票信息不存在，请重新选择");
        return;
      }
      this.selectedInvoiceProfileId = selected.id;
      this.selectedInvoiceProfile = { ...selected };
      this.invoiceForm = this.buildInvoiceFormByProfile(selected);
      this.invoiceChoice = "need";
      this.needInvoice = true;
      this.invoiceProfileDialogVisible = false;
      this.$message.success("已选择开票信息");
    },

    openInvoiceProfileForm(item) {
      const userInfo = this.$store?.state?.user?.userInfo || {};
      const customerId = this.getCurrentUserId();
      if (item) {
        this.invoiceProfileForm = { ...DEFAULT_INVOICE_PROFILE_FORM, ...item };
        this.invoiceProfileMainSwitch = item.isMain === "Y";
      } else {
        this.invoiceProfileForm = {
          ...DEFAULT_INVOICE_PROFILE_FORM,
          customerId,
          buyerName: userInfo.realName || userInfo.username || "",
          buyerPhone: userInfo.phonenum || userInfo.phone || "",
          email: userInfo.email || "",
        };
        this.invoiceProfileMainSwitch = false;
      }
      this.invoiceProfileFormVisible = true;
      this.$nextTick(() => {
        if (this.$refs.invoiceProfileFormRef) {
          this.$refs.invoiceProfileFormRef.clearValidate();
        }
      });
    },

    syncInvoiceProfileMainStatus(val) {
      this.invoiceProfileForm.isMain = val ? "Y" : "N";
    },

    submitInvoiceProfileForm() {
      if (!this.$refs.invoiceProfileFormRef) return;
      this.$refs.invoiceProfileFormRef.validate(async (valid) => {
        if (!valid) return;
        this.invoiceProfileSubmitting = true;
        try {
          const payload = {
            ...this.invoiceProfileForm,
            customerId: this.getCurrentUserId(),
            isMain: this.invoiceProfileMainSwitch ? "Y" : "N",
          };
          const res = await modifyInvoiceProfile(payload);
          if (res.code !== 200) {
            this.$message.error(res.msg || "保存开票信息失败");
            return;
          }
          this.$message.success("保存开票信息成功");
          this.invoiceProfileFormVisible = false;
          await this.loadInvoiceProfiles();
        } finally {
          this.invoiceProfileSubmitting = false;
        }
      });
    },

    async setDefaultInvoiceProfileAction(item) {
      const ok = await this.$confirm("确认将该条记录设为默认开票信息吗？", "提示", {
        type: "warning",
      }).catch(() => false);
      if (!ok) return;
      const res = await setDefaultInvoiceProfile(item.id);
      if (res.code !== 200) {
        this.$message.error(res.msg || "设置默认失败");
        return;
      }
      this.$message.success("设置默认成功");
      await this.loadInvoiceProfiles();
    },

    async removeInvoiceProfileAction(item) {
      const ok = await this.$confirm("确认删除该条开票信息吗？", "提示", {
        type: "warning",
      }).catch(() => false);
      if (!ok) return;
      const res = await removeInvoiceProfile(item.id);
      if (res.code !== 200) {
        this.$message.error(res.msg || "删除开票信息失败");
        return;
      }
      this.$message.success("删除开票信息成功");
      if (this.invoiceProfileDraftId === item.id) {
        this.invoiceProfileDraftId = "";
      }
      if (this.selectedInvoiceProfileId === item.id) {
        this.selectedInvoiceProfileId = "";
        this.selectedInvoiceProfile = null;
        this.invoiceForm = { ...DEFAULT_INVOICE_FORM };
      }
      await this.loadInvoiceProfiles();
    },

    // 统一社会信用代码校验：18位，大写字母或数字。
    // 排除 I、O、Z、S、V，符合常见统一社会信用代码字符集。
    validateSocialCreditCode(code) {
      const value = String(code || "").trim().toUpperCase();
      const reg = /^[0-9A-HJ-NPQRTUWXY]{18}$/;
      return reg.test(value);
    },

    // 输入时自动转大写，只影响当前下单页输入框。
    handleCreditCodeInput(value) {
      this.invoiceForm.ratenumber = String(value || "").trim().toUpperCase();
      this.invoiceForm.companysocialcreditcode = this.invoiceForm.ratenumber;
    },


    // 点击“更新”按钮时，同时把发票抬头和统一信用代码同步到用户认证信息。
    // 不点击该按钮时，下单页里的发票抬头、统一信用代码只作为本次下单数据使用。
    async updateSocialCreditCodeToUserCenter() {
      if (!this.invoiceForm.company) {
        this.$message.warning("请先填写发票抬头");
        return;
      }

      if (!this.invoiceForm.ratenumber) {
        this.$message.warning("请先填写统一社会信用代码");
        return;
      }

      if (!this.validateSocialCreditCode(this.invoiceForm.ratenumber)) {
        this.$message.warning("统一社会信用代码需为18位大写字母或数字");
        return;
      }

      const userInfo = this.$store?.state?.user?.userInfo || {};
      if (!userInfo.id) {
        this.$message.warning("未获取到用户信息，请重新登录后再试");
        return;
      }

      this.creditUpdating = true;

      try {
        await request({
          url: "/api/business/C_customers/" + userInfo.id,
          method: "PUT",
          data: {
            ...userInfo,

            // 发票抬头同步到用户认证信息里的公司名称
            companyname: this.invoiceForm.company,

            // 统一信用代码同步到用户认证信息里的社会统一信用代码
            companysocialcreditcode: this.invoiceForm.ratenumber,
          },
        });

        await this.$store.dispatch("user/getInfo");
        this.$message.success("发票抬头和统一信用代码已更新");
      } catch (error) {
        this.$message.error("发票抬头和统一信用代码更新失败");
      } finally {
        this.creditUpdating = false;
      }
    },

    // 免费版资料完整性校验。
    validateUserRequiredInfo() {
      const missing = [];
      const creditCode =
          this.invoiceForm.ratenumber || this.invoiceForm.companysocialcreditcode;

      if (!this.receiverInfo.name) missing.push("姓名");
      if (!this.receiverInfo.phone) missing.push("手机号");
      if (!this.invoiceForm.company && !this.receiverInfo.company) {
        missing.push("发票抬头/公司名称");
      }

      if (!creditCode) {
        missing.push("统一社会信用代码");
      } else if (!this.validateSocialCreditCode(creditCode)) {
        missing.push("18位有效统一社会信用代码");
      }

      if (!this.invoiceForm.address && !this.receiverInfo.address) {
        missing.push("地址");
      }

      if (missing.length) {
        this.$message.warning(`请先完善以下信息：${missing.join("、")}`);
        return false;
      }

      return true;
    },

    // 下单时同步用户中心资料。
    // 注意：这里故意不更新 companysocialcreditcode。
    // 统一信用代码只允许通过“更新”按钮同步到用户认证界面。
    async syncUserCenterInfo() {
      const userInfo = this.$store?.state?.user?.userInfo || {};
      if (!userInfo.id) return;

      const data = {
        ...userInfo,
        username: this.receiverInfo.name,
        phonenum: this.receiverInfo.phone,
        email: this.receiverInfo.email,
        companyname: this.invoiceForm.company || this.receiverInfo.company,
        address: this.invoiceForm.address || this.receiverInfo.address,

        // 保持用户认证界面的统一信用代码不被下单页默认覆盖。
        companysocialcreditcode: userInfo.companysocialcreditcode || "",
      };

      await request({
        url: "/api/business/C_customers/" + userInfo.id,
        method: "PUT",
        data,
      });

      await this.$store.dispatch("user/getInfo");
    },

    async handleConfirm() {
      // 防抖：提交中禁止重复点击
      if (this.submitting) return;

      if (!this.isFree && !this.invoiceChoice) {
        this.$message.warning("请选择是否开发票");
        return;
      }

      if (!this.isFree && this.invoiceChoice === "need" && !this.selectedInvoiceProfile) {
        this.$message.warning("请选择开票信息");
        await this.openInvoiceProfileDialog();
        return;
      }

      const valid = await this.$refs.receiverForm.validate().catch(() => false);
      if (!valid) return;

      if (!this.editableOrderInfo.length) {
        this.$message.warning("订单商品不能为空");
        return;
      }

      // 提交前重新校验，避免优惠码失焦校验尚未完成时携带无效代码下单。
      if (this.inviteCode && !(await this.checkCode(false))) {
        return;
      }

      const currentUser = this.$store?.state?.user?.userInfo || {};
      const blacklistResult = await checkBlacklist({
        customerId: String(currentUser.id || currentUser.userId || ""),
        companyName:
          this.invoiceChoice === "need"
            ? this.selectedInvoiceProfile?.buyerName || this.receiverInfo.company
            : this.receiverInfo.company,
      });
      if (!blacklistResult || blacklistResult.code !== 200) {
        this.$message.error(
          (blacklistResult && blacklistResult.msg) || "当前用户禁止购买"
        );
        return;
      }

      // 重新点击确定（重试）时，先清空已生成的二维码并停止旧的轮询，
      // 让二维码区域重新进入 loading 状态，避免用户误以为还是旧的二维码
      this.paymentQrcode = "";
      this.stopPolling();

      this.submitting = true;

      try {
        // 下单前同步用户中心资料，但不会默认同步统一社会信用代码。
        await this.syncUserCenterInfo();

        const userInfo = this.$store?.state?.user?.userInfo || {};
        const orderItems = this.editableOrderInfo;

        const detail = orderItems.map((item) => {
          this.normalizeDurationItem(item);

          return {
            dogState: 1,
            durationunion: item.durationunion || "week",
            duration: Number(item.duration) || 1,
            featureid: item.productInfo?.profeatureid || null,
            number: Number(item.quantity || item.number) || 1,
            price: this.isFree ? 0 : this.getItemUnitPrice(item),
            productcode:
                item.productInfo?.typeItem?.code || item.production?.code || "",
            productid: item.productInfo?.productId || "",
            productname: item.productInfo?.typeItem?.name || item.production?.name || item.productInfo?.name || "",
            producttype:
                item.productInfo?.typeItem?.type || item.production?.type || item.producttype || "",
            productVersionType: item.productInfo?.type || "",
          };
        });

        let ordername = "聚合订单";
        if (orderItems.length === 1) {
          const item = orderItems[0];
          ordername = `${item.productInfo?.typeItem?.name || item.production?.name || ""}${item.productInfo?.name || ""}`;
        }

        const payload = {
          ordername: `${ordername}`,
          paymentstate: this.isFree ? "1" : "0",
          productDetails: detail,
          paymentamount: this.isFree ? 0 : Number(this.finalPrice || 0),
          baseamount: this.isFree ? 0 : Number(this.totalPrice || 0),
          purchasemethod: "online",
          userid: String(userInfo.id || ""),
          username: userInfo.username || "",
          receiveraddress:
              this.receiverInfo.address || this.invoiceForm.address || "",
          receiveremail: this.receiverInfo.email,
          receivername: this.receiverInfo.name,
          receiverphone: this.receiverInfo.phone,
          company:
            this.invoiceChoice === "need"
              ? this.selectedInvoiceProfile?.buyerName || this.receiverInfo.company
              : this.receiverInfo.company,
          retailercode: this.inviteCode || "",
          ...(this.invoiceChoice === "need"
            ? {
                isinvoice: 1,
                invoiceProfileId: String(this.selectedInvoiceProfileId || ""),
              }
            : {}),
          invoiceForm: this.invoiceChoice === "need"
              ? {
                  ...this.buildInvoiceFormByProfile(this.selectedInvoiceProfile),
                  companysocialcreditcode:
                    this.selectedInvoiceProfile?.buyerTaxNo ||
                    this.selectedInvoiceProfile?.companysocialcreditcode ||
                    "",
              }
              : null,
          orderRemark: this.orderRemark,
          paySource: orderItems[0].pvData ? "2" : "1",
          cartIds: orderItems[0].pvData
              ? orderItems.map((item) => item.id).join(",")
              : "",
        };

        const res = await createOrder(payload);
        const ok =
            res && (res.success || res.code === 200 || res.status === "ok");

        if (!ok) {
          throw new Error(
              res.message || this.$t("OrderConfirmDialog.orderFailed")
          );
        }

        const data = res.data || res;
        this.orderStatus = data;

        if (this.isFree) {
          this.paymentSuccess = true;
          this.startAfterPayGuide();
          return;
        }

        const orderNum = data.ordernum || "";
        const price = Number(data.price || this.finalPrice || 0);
        const title = data.title || payload.ordername;

        if (!orderNum) {
          throw new Error(this.$t("OrderConfirmDialog.missingOrderId"));
        }

        // 订单创建成功后立即展示付款区域，二维码生成期间显示 loading
        this.showPayment = true;

        try {
          const qr = await getCmbQrCode({ orderNum, price, title });

          if (!qr || qr.code !== 200 || !qr.msg) {
            throw new Error(
                (qr && qr.msg) || this.$t("OrderConfirmDialog.getQrcodeFailed")
            );
          }

          this.paymentQrcode = qr.msg;

          this.$message.success(this.$t("OrderConfirmDialog.orderSuccess"));
          this.startPolling();
        } catch (qrErr) {
          // 二维码生成失败：弹框提示错误信息，隐藏付款区域，等待用户重新点击确定
          this.showPayment = false;
          this.paymentQrcode = "";
          this.$alert(
              qrErr.message || this.$t("OrderConfirmDialog.getQrcodeFailed"),
              "提示",
              {
                confirmButtonText: "确定",
                type: "error",
              }
          );
        }
      } catch (err) {
        this.$alert(
            err.message || this.$t("OrderConfirmDialog.orderError"),
            "提示",
            {
              confirmButtonText: "确定",
              type: "error",
            }
        );
      } finally {
        // 无论成功失败都重置提交状态，确定按钮恢复可点击
        this.submitting = false;
      }
    },

    startPolling() {
      this.stopPolling();

      this.intervalPay = setInterval(() => {
        this.checkPay();
      }, 2000);

      setTimeout(() => {
        if (this.intervalPay) {
          clearInterval(this.intervalPay);
          this.intervalPay = null;

          if (!this.paymentSuccess) {
            this.$message.warning(
                this.$t("OrderConfirmDialog.paymentTimeout") ||
                "支付超时，请重新下单"
            );
          }
        }
      }, 15 * 60 * 1000);
    },

    checkPay() {
      if (!this.orderStatus.id) return;

      getOrderMes(this.orderStatus.id)
          .then((res) => {
            const orderData = res.data || res;

            if (orderData.paymentstate == 1) {
              this.stopPolling();
              this.paymentSuccess = true;
              this.startAfterPayGuide();
              this.$message.success(
                  this.$t("OrderConfirmDialog.paymentCompleted")
              );
            }
          })
          .catch((err) => {
            console.warn("检查支付状态失败", err);
          });
    },

    stopPolling() {
      if (this.intervalPay) {
        clearInterval(this.intervalPay);
        this.intervalPay = null;
      }
    },

    async handlePay() {
      if (!this.orderStatus.id) {
        this.$message.warning(this.$t("OrderConfirmDialog.orderIdMissing"));
        return;
      }

      this.paying = true;

      try {
        const orderRes = await getOrderMes(this.orderStatus.id);
        const orderData = orderRes.data || orderRes;
        const paid = orderData.paymentstate == "1";

        if (paid) {
          this.stopPolling();
          this.paymentSuccess = true;
          this.startAfterPayGuide();
          this.$message.success(this.$t("OrderConfirmDialog.paymentCompleted"));
        } else {
          this.$message.info(this.$t("OrderConfirmDialog.paymentNotDetected"));
        }
      } catch (error) {
        this.$message.error(
            error.message || this.$t("OrderConfirmDialog.queryPaymentFailed")
        );
      } finally {
        this.paying = false;
      }
    },

    // 支付成功后启动购买后引导（PHASE 0：高亮「个人中心查看」入口）
    startAfterPayGuide() {
      this.$nextTick(() => {
        // 等待支付成功页 DOM 渲染，再延迟一点启动引导，避免动画抖动
        setTimeout(() => {
          startAfterPayGuidePersonal(() => {
            // 引导"下一步"回调：关闭弹框并跳转到个人中心订单管理 tab
            this.goToUserCenter();
          });
        }, 300);
      });
    },

    goToUserCenter() {
      // 引导激活时直点链接：视为完成 PHASE 0，推进到 PHASE 1（订单下载引导）
      // 否则销毁可能残留的引导遮罩
      if (getAfterPayPhase() === 0) {
        destroyAfterPayGuide({ keepPhase: true });
        setAfterPayPhase(1);
      } else {
        destroyAfterPayGuide();
      }
      this.stopPolling();
      this.dialogVisible = false;
      this.$router.push({ path: "/PersonalCenter", query: { value: 'order' } });
    },

    handleCancel() {
      this.stopPolling();
      this.$emit("cancel");
      this.dialogVisible = false;
    },

    handleClose() {
      if (this.paying) {
        this.$message.warning(this.$t("OrderConfirmDialog.payingInProgress"));
        return;
      }

      // 关闭弹框时若引导仍在进行，一并销毁，避免遮罩滞留
      destroyAfterPayGuide();
      this.stopPolling();
      this.dialogVisible = false;
    },

    async checkCode(showSuccess = true) {
      const code = (this.inviteCode || "").trim();
      const shouldShowSuccess = typeof showSuccess === "boolean" ? showSuccess : true;

      if (!code) {
        this.clearInviteCode();
        return true;
      }

      try {
        const res = await C_retailers({ retailcode: code });
        const list = res?.data?.list || res?.list || [];

        if (list.length !== 1) {
          this.$message.warning(
              this.$t("OrderConfirmDialog.inviteCodeInvalid")
          );
          this.clearInviteCode();
          return false;
        }

        const item = list[0];
        this.retailerProductids = (item.productid || "")
            .split(",")
            .map((productId) => String(productId).trim())
            .filter(Boolean);
        this.retailCount = Number(item.discount) || 1;

        const hasMatchedProduct = this.editableOrderInfo.some((orderItem) => {
          const productId = this.getItemProductId(orderItem);
          return productId && this.retailerProductids.includes(productId);
        });

        if (!hasMatchedProduct) {
          this.$message.warning(
              this.$t("OrderConfirmDialog.inviteCodeInvalid")
          );
          this.clearInviteCode();
          return false;
        }

        // 仅匹配优惠码绑定产品的商品享受折扣，购物车内其他商品保持原价。
        this.recalculateDiscountAmount();

        // 邀请码有效且金额发生变化：若此前已生成二维码/已展示付款区域，
        // 需要重置，提示用户重新点击"确定"以按最新折扣价格重新生成订单和二维码
        if (this.paymentQrcode || this.showPayment) {
          this.paymentQrcode = "";
          this.showPayment = false;
          this.stopPolling();
          this.$message.warning(
              this.$t("OrderConfirmDialog.inviteCodeChangedNeedReconfirm")
          );
        } else if (shouldShowSuccess) {
          this.$message.success(this.$t("OrderConfirmDialog.inviteCodeSuccess"));
        }
        return true;
      } catch (err) {
        this.clearInviteCode();
        this.$message.error(
            err?.message || this.$t("OrderConfirmDialog.inviteCodeFailed")
        );
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s;
}

.slide-left-enter,
.slide-left-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.payment-success {
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;

  .success-content {
    text-align: center;
  }

  .success-icon .el-icon-success {
    font-size: 64px;
    color: #1e88e5;
  }

  .success-title {
    font-size: 24px;
    font-weight: 500;
    color: #333;
    margin: 24px 0 16px;
  }

  .view-link {
    font-size: 14px;
    color: #1e88e5;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.order-container {
  display: flex;
  min-height: 600px;
}

.order-left {
  flex: 1;
  min-width: 0;
  padding: 15px 25px;
  overflow: hidden;
}

.order-right {
  flex: 0 0 280px;
  padding: 24px;
  background: #fafafa;
  border-left: 1px solid #eee;
}

.section {
  margin-bottom: 24px;
  box-shadow: 0 2px 10px 15px #f8f9fa;
  border-radius: 8px;
  padding: 15px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }
}

.order-section {
  overflow-x: auto;
}

.order-table {
  min-width: 820px;
  border: 1px solid #f3f3f3;
  border-radius: 8px;
  overflow: hidden;

  .table-header {
    display: flex;
    background: #f5f5f5;
    padding: 12px 16px;
    font-size: 14px;
    color: #666;
    text-align: center;
  }

  .table-body {
    padding: 16px;
    text-align: center;
  }
}

.order-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.col-product {
  width: 220px;
  flex-shrink: 0;
}

.col-detail {
  flex: 1;
  min-width: 120px;
}

.col-duration {
  width: 170px;
  text-align: center;
}

.col-quantity {
  width: 120px;
  text-align: center;
}

.col-price {
  width: 120px;
  text-align: right;
}

.duration-editor {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;

  .el-input-number--mini {
    width: 88px;
  }

  .duration-unit-select {
    width: 70px;
  }
}

.product-info {
  display: flex;
  align-items: center;
  gap: 12px;

  .product-logo {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .product-name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    color: #333;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.detail-text {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.price-info {
  .current-price {
    font-size: 16px;
    font-weight: 600;
    color: #ff6b6b;
  }

  .discount-info {
    margin-top: 4px;
    font-size: 12px;
    color: #999;

    .discount-amount {
      color: #ff6b6b;
      margin-left: 4px;
    }
  }
}

.form-row {
  background: #f8f9fa;
  padding: 16px 20px 3px;

  .form-label {
    width: 100%;
    font-weight: 600;
    font-size: 13px;
    color: #000;
    line-height: 32px;
    flex-shrink: 0;
    display: block;
  }
}

.invite-input {
  width: 200px;
}

.invoice-detail {
  background: #f9f9f9;
  padding: 20px 20px 0;

  .invoice-row {
    display: flex;
    align-items: center;
    margin-bottom: 18px;
    font-size: 12px;

    &:last-child {
      margin-bottom: 0;
    }

    .form-label {
      width: 85px;
    }
  }

  .invoice-profile-select-row {
    justify-content: space-between;
  }

  .invoice-profile-preview {
    background: #fff;
    border: 1px solid #e8ebf0;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 18px;

    .preview-row {
      display: flex;
      line-height: 24px;
      font-size: 12px;
      color: #606266;

      .preview-label {
        width: 90px;
        color: #909399;
        flex-shrink: 0;
      }

      .preview-value {
        color: #303133;
        word-break: break-all;
      }
    }
  }
}

.invoice-credit-row,
.invoice-header-row {
  align-items: flex-start !important;

  > .form-label {
    padding-top: 6px;
    width: 85px;
    flex-shrink: 0;
  }

  .invoice-credit-content,
  .invoice-header-content {
    flex: 1;
  }

  .invoice-credit-actions,
  .invoice-header-actions {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-top: 6px;
    min-height: 26px;
  }

  .credit-error {
    color: #f56c6c;
    font-size: 12px;
    line-height: 24px;
  }

  .credit-update-btn,
  .header-update-btn {
    margin-left: auto;
    padding: 5px 13px;
    font-size: 12px;
    border-radius: 3px;
  }
}

.form-actions {
  padding: 20px;
  text-align: left;
  background: #f8f9fa;
}

.payment-section {
  .payment-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 4px;
  }

  .product-count {
    font-size: 12px;
    color: #999;
    margin: 0 0 16px;
  }
}

.price-summary {
  margin-bottom: 20px;

  .price-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 14px;
    color: #666;

    &.discount .discount-price {
      color: #ff6b6b;
    }

    &.total {
      padding-top: 12px;
      border-top: 1px solid #eee;
      font-weight: 600;

      .final-price {
        font-size: 20px;
        color: #ff6b6b;
      }
    }
  }
}

.qrcode-section {
  text-align: center;
  margin-bottom: 16px;

  .qrcode-wrapper {
    width: 140px;
    height: 140px;
    margin: 0 auto 8px;
    border: 1px solid #eee;
    border-radius: 8px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
    }

    .qrcode-loading {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: #ccc;
    }
  }

  .qrcode-tip {
    font-size: 12px;
    color: #999;
    margin: 0;
  }
}

.payment-methods {
  display: flex;
  justify-content: space-around;
  gap: 16px;
  margin-bottom: 16px;

  .method-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    border-radius: 6px;
    transition: all 0.2s;

    img {
      width: 28px;
      height: 28px;
    }

    span {
      font-size: 12px;
      color: #666;
    }
  }
}

.pay-tip {
  width: 100%;
  height: 44px;
  line-height: 44px;
  text-align: center;
  font-size: 16px;
  color: #999;
  background: #f5f7fa;
  border-radius: 8px;
  user-select: none;
  pointer-events: none;
}

.fake-checkbox {
  display: inline-flex;
  align-items: center;
  margin-right: 20px;
  cursor: pointer;
  user-select: none;

  .checkbox-icon {
    width: 14px;
    height: 14px;
    border: 1px solid #dcdfe6;
    border-radius: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 6px;
    background: #fff;
    transition: all 0.2s;

    &.checked {
      background: #409eff;
      border-color: #409eff;

      i {
        color: #fff;
        font-size: 12px;
        font-weight: bold;
      }
    }
  }

  .checkbox-label {
    font-size: 14px;
    color: #606266;
  }

  &:hover .checkbox-icon {
    border-color: #409eff;
  }
}
</style>

<style lang="scss">
.order-confirm-dialog {
  background-color: #f8f9fa;
  width: 92vw !important;
  max-width: 1200px;
  min-width: 960px;
  // 始终在视口内垂直居中：覆盖 el-dialog 默认的 15vh 上边距（内联样式需 !important），
  // 配合位移实现任意窗口高度下居中，body 已限高，确保底部确定按钮无需滚动即可见
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  top: 50%;
  transform: translateY(-50%);

  .el-dialog__header {
    padding: 16px 20px;
  }

  .el-dialog__body {
    padding: 0 !important;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
}

.invoice-profile-select-dialog {
  .el-dialog__body {
    padding-top: 12px;
  }

  .invoice-profile-dialog-actions {
    margin-bottom: 12px;
  }

  .invoice-profile-card-list {
    max-height: 460px;
    overflow-y: auto;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .invoice-profile-card {
    position: relative;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px 12px 12px 34px;
    transition: all 0.2s;
    cursor: pointer;

    &.active {
      border-color: #409eff;
      box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
    }

    .profile-select-indicator {
      position: absolute;
      top: 10px;
      left: 10px;
      width: 14px;
      height: 14px;
      border: 1px solid #dcdfe6;
      border-radius: 2px;
      background: #fff;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      &.checked {
        background: #409eff;
        border-color: #409eff;

        i {
          color: #fff;
          font-size: 12px;
          font-weight: 700;
        }
      }
    }

    .profile-head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;
    }

    .profile-title-wrap {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    .profile-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      max-width: 260px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .profile-actions {
      white-space: nowrap;

      .el-button {
        padding: 0;
        margin-left: 8px;
      }

      .danger {
        color: #f56c6c;
      }
    }

    .profile-row {
      display: flex;
      line-height: 22px;
      font-size: 12px;

      .label {
        width: 90px;
        color: #909399;
        flex-shrink: 0;
      }

      .value {
        color: #606266;
        word-break: break-all;
      }
    }
  }
}
</style>
